/**
 * Cliente HTTP minimalista pra chamar a API ChatPlus.
 * Anexa Bearer token automaticamente, injeta credentials pra cookie refresh,
 * e levanta erros tipados a partir de Problem Details RFC 7807.
 */

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  code: string;
  trace_id?: string;
  context?: Record<string, unknown>;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(public readonly problem: ProblemDetails) {
    super(problem.title);
  }
  get code(): string {
    return this.problem.code;
  }
  get status(): number {
    return this.problem.status;
  }
}

const DEFAULT_BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';

let accessTokenGetter: () => string | null = () => null;

export function setAccessTokenGetter(getter: () => string | null): void {
  accessTokenGetter = getter;
}

/** Acesso direto ao token — uso pra fetches que precisam de body custom (multipart). */
export function getCurrentAccessToken(): string | null {
  return accessTokenGetter();
}

export const API_BASE_URL = DEFAULT_BASE_URL;

/**
 * Handler de refresh registrado pelo auth-store. Chamado quando uma request
 * autenticada recebe 401 — deve trocar o access token (via cookie HTTP-only
 * de refresh) e retornar `true` em sucesso. Em `false`, a request original
 * falha (refresh expirou também → usuário precisa logar de novo).
 */
type RefreshHandler = () => Promise<boolean>;
let refreshHandler: RefreshHandler | null = null;
let inFlightRefresh: Promise<boolean> | null = null;

export function setRefreshHandler(handler: RefreshHandler | null): void {
  refreshHandler = handler;
}

/**
 * Single-flight refresh: várias requests que dão 401 simultaneamente coalescem
 * numa única chamada. Todas esperam o mesmo Promise e retentam quando ele resolve.
 */
/**
 * Renova o crachá de acesso agora, pelo MESMO caminho do 401 (single-flight:
 * várias chamadas juntas viram um refresh só). Usado pelo socket quando o
 * servidor recusa a conexão em tempo real por acesso vencido.
 */
export function renovarAcessoAgora(): Promise<boolean> {
  return performRefresh();
}

async function performRefresh(): Promise<boolean> {
  if (!refreshHandler) return false;
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = refreshHandler().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Se true, NÃO envia Authorization header (login, refresh) */
  skipAuth?: boolean;
  /**
   * Requisição que precisa sobreviver ao descarregar da página (F5, fechar
   * aba): o navegador termina de enviá-la mesmo sem a página. Só pra avisos
   * curtos e sem corpo grande (o navegador limita a 64 KB).
   */
  keepalive?: boolean;
  /** Interno — flag pra evitar loop de retry após refresh. */
  _retried?: boolean;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    skipAuth = false,
    keepalive = false,
    _retried = false,
  } = options;
  const url = `${DEFAULT_BASE_URL}${path}`;

  // Só seta Content-Type: application/json quando há body — Fastify rejeita
  // requests sem body declarando esse header (FST_ERR_CTP_EMPTY_JSON_BODY).
  const finalHeaders: Record<string, string> = {
    ...(body !== undefined && { 'Content-Type': 'application/json' }),
    ...headers,
  };
  if (!skipAuth) {
    const token = accessTokenGetter();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    credentials: 'include', // pra cookie refresh
    ...(keepalive && { keepalive: true }),
    ...(body !== undefined && { body: JSON.stringify(body) }),
  });

  // Auto-refresh em 401: token expirou no meio da sessão. Tenta renovar via
  // cookie HTTP-only de refresh e reexecuta a request UMA vez. `skipAuth` cobre
  // os próprios endpoints de auth, e `_retried` evita loop infinito.
  if (response.status === 401 && !skipAuth && !_retried) {
    const refreshed = await performRefresh();
    if (refreshed) {
      return apiRequest<T>(path, { ...options, _retried: true });
    }
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    if (data && typeof data === 'object' && 'code' in data) {
      throw new ApiError(data as ProblemDetails);
    }
    throw new ApiError({
      type: 'about:blank',
      title: 'Erro na requisição',
      status: response.status,
      code: 'UNKNOWN_ERROR',
    });
  }

  return data as T;
}
